import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Calculator,
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  Eye,
  HelpCircle,
  Layers,
  Minus,
  Save,
  Sparkles,
  Wand2,
} from "lucide-react";
import API from "../../../../services/api";
import { v2ListComponents, v2ValidateFormula } from "../SalaryApiV2";
import { filterComponentsForPipeline } from "./components/pipelineComponentFilter";
import { entGetCatalog, entSimulateFormula } from "../salaryEnterpriseApi";
import { useToast } from "../../../Context/ToastContext";
import SalaryTemplatePipelineBar from "./components/SalaryTemplatePipelineBar";
import StructureGrossPanel from "./components/StructureGrossPanel";
import {
  PIPELINE_STEPS,
  isPipelineActive,
  parsePipelineStep,
  pipelineStepPath,
  readPipelineState,
  writePipelineState,
} from "./components/salaryTemplatePipeline";
import { componentCode, formulaOf, mappingText } from "../wizard/shared";
import { readableFormula } from "./components/buildGrossAllocation";
import { isEarningSection, isVariablePayrollComponent } from "./components/salaryComponentModel";
import PayrollComponentToolsBar from "./components/PayrollComponentToolsBar";
import {
  applyFormulaToComponentAndReturn,
  componentEditPath,
  parseComponentToolSearchParams,
} from "./components/componentFormulaNav";
import "../../../../styles/formula-builder-premium.css";
import "../../../../styles/salary-template-pipeline.css";
import "../../../../styles/salary-templates-premium.css";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const SAMPLE_CTX = {
  BASIC: 50000,
  HRA: 20000,
  GROSS: 80000,
  CTC: 1200000,
  MONTHLY_CTC: 100000,
  ANNUAL_CTC: 1200000,
  WORKING_DAYS: 26,
  PAID_DAYS: 24,
  LOP_DAYS: 2,
  OT_HOURS: 10,
};

function money(n) {
  if (!Number.isFinite(Number(n))) return "—";
  return INR.format(Math.round(Number(n)));
}

const FORMULA_TYPES = [
  { id: "earnings", label: "Earnings", desc: "Basic, Gross", icon: CircleDollarSign },
  { id: "allowance", label: "Allowance", desc: "HRA, Medical, etc.", icon: Sparkles },
  { id: "deduction", label: "Deduction", desc: "PF, ESI, Loan", icon: Minus },
  { id: "employer", label: "Employer Contribution", desc: "Company share", icon: Layers },
  { id: "custom", label: "Custom Formula", desc: "Any calculation", icon: Wand2 },
];

const VARIABLE_META = {
  BASIC: { label: "Basic Salary", category: "Salary" },
  GROSS: { label: "Monthly Gross", category: "Salary" },
  MONTHLY_CTC: { label: "Monthly Gross", category: "Salary" },
  ANNUAL_CTC: { label: "Annual Gross", category: "Salary" },
  CTC: { label: "Annual Gross", category: "Salary" },
  NET: { label: "Net Salary", category: "Salary" },
  NET_SALARY: { label: "Net Salary", category: "Salary" },
  WORKING_DAYS: { label: "Working Days", category: "Attendance" },
  PAID_DAYS: { label: "Paid Days", category: "Attendance" },
  LOP_DAYS: { label: "LOP Days", category: "Attendance" },
  OT_HOURS: { label: "OT Hours", category: "Attendance" },
  HRA: { label: "HRA", category: "Allowances" },
  CONVEYANCE: { label: "Conveyance", category: "Allowances" },
  MEDICAL: { label: "Medical", category: "Allowances" },
  INTERNET: { label: "Internet", category: "Allowances" },
  SPECIAL_ALLOWANCE: { label: "Special Allowance", category: "Allowances" },
  SPECIAL: { label: "Special Allowance", category: "Allowances" },
  PF: { label: "PF", category: "Deductions" },
  ESI: { label: "ESI", category: "Deductions" },
  PT: { label: "Professional Tax", category: "Deductions" },
  PROFESSIONAL_TAX: { label: "Professional Tax", category: "Deductions" },
  LOAN: { label: "Loan", category: "Deductions" },
  LOAN_RECOVERY: { label: "Loan", category: "Deductions" },
  ADVANCE: { label: "Advance", category: "Deductions" },
  TOTAL_DEDUCTIONS: { label: "Total Deductions", category: "Deductions" },
  NPS: { label: "NPS", category: "Deductions" },
  INSURANCE: { label: "Insurance", category: "Deductions" },
  PERFORMANCE_SCORE: { label: "Performance Score", category: "Performance" },
  UNITS_SOLD: { label: "Units Sold", category: "Performance" },
  SHIFT_COUNT: { label: "Shift Count", category: "Performance" },
};

const CATEGORY_ORDER = ["Salary", "Attendance", "Allowances", "Deductions", "Performance", "Other values"];
const CATEGORY_COLORS = {
  Salary: "#2563eb",
  Attendance: "#0d9488",
  Allowances: "#7c3aed",
  Deductions: "#dc2626",
  Performance: "#d97706",
  "Other values": "#64748b",
};

const FALLBACK_VARIABLES = [
  "BASIC",
  "HRA",
  "GROSS",
  "CTC",
  "ANNUAL_CTC",
  "MONTHLY_CTC",
  "WORKING_DAYS",
  "PAID_DAYS",
  "LOP_DAYS",
  "OT_HOURS",
  "UNITS_SOLD",
  "PERFORMANCE_SCORE",
  "TOTAL_DEDUCTIONS",
  "NPS",
  "INSURANCE",
  "SPECIAL_ALLOWANCE",
];

const TEMPLATES = [
  { id: "percentage", label: "Percentage", hint: "Basic × 40%", expression: "BASIC * 0.40" },
  { id: "fixed", label: "Fixed Amount", hint: "₹5,000 flat", expression: "5000" },
  { id: "conditional", label: "Conditional", hint: "If / Then / Else", expression: "if(GROSS > 50000, 5000, 2500)" },
  { id: "addition", label: "Addition", hint: "Basic + HRA", expression: "BASIC + HRA" },
  { id: "subtraction", label: "Subtraction", hint: "Gross − Deductions", expression: "GROSS - TOTAL_DEDUCTIONS" },
  { id: "multiply", label: "Multiply", hint: "OT × Rate", expression: "OT_HOURS * 200" },
  { id: "divide", label: "Divide", hint: "CTC ÷ 12", expression: "ANNUAL_CTC / 12" },
  { id: "advanced", label: "Advanced Formula", hint: "Use functions", expression: null },
];

const POPULAR_EXAMPLES = [
  { label: "HRA = Basic × 40%", expression: "BASIC * 0.40" },
  { label: "PF = Basic × 12%", expression: "BASIC * 0.12" },
  { label: "Bonus = Fixed ₹5,000", expression: "5000" },
  { label: "Overtime = OT Hours × Hourly Rate", expression: "OT_HOURS * 200" },
];

const PROGRESS_STEPS = [
  { id: 1, label: "Choose Formula Type" },
  { id: 2, label: "Build Formula" },
  { id: 3, label: "Preview & Validate" },
];

function friendlyLabel(code) {
  return VARIABLE_META[code]?.label || String(code).replace(/_/g, " ");
}

function groupVariables(codes) {
  const groups = {};
  for (const code of codes) {
    const category = VARIABLE_META[code]?.category || "Other values";
    if (!groups[category]) groups[category] = [];
    groups[category].push(code);
  }
  return CATEGORY_ORDER.filter((c) => groups[c]?.length).map((category) => ({
    category,
    color: CATEGORY_COLORS[category],
    codes: groups[category],
  }));
}

function componentFormula(comp, formulas) {
  const id = String(comp?.component_id || "");
  return String(formulas?.[id]?.expression || formulaOf(comp) || "").trim();
}

function componentNeedsFormula(comp, expression) {
  if (!comp || isVariablePayrollComponent(comp)) return false;
  const calc = String(comp.calculation_type || "").toLowerCase();
  if (calc === "system" && String(comp.system_code || "").trim()) return false;
  return !String(expression || "").trim();
}

export default function SalaryFormulaBuilder() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pipelineStep = parsePipelineStep(searchParams);
  const pipelineFormulas = isPipelineActive() && pipelineStep === PIPELINE_STEPS.formulas;
  const toolCtx = useMemo(() => parseComponentToolSearchParams(searchParams), [searchParams]);
  const editingComponent = Boolean(toolCtx.componentId) && !pipelineFormulas;
  const primedRef = useRef(false);

  const [expr, setExpr] = useState(() => (pipelineFormulas ? "" : "if(GROSS > 50000, 5000, 2500)"));
  const [pipelineComps, setPipelineComps] = useState([]);
  const [pipelineActiveId, setPipelineActiveId] = useState(null);
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [monthlyGross, setMonthlyGross] = useState(() => {
    const annual = Number(readPipelineState().targetAnnualCtc);
    return annual > 0 ? String(Math.round(annual / 12)) : "";
  });
  const [validation, setValidation] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formulaType, setFormulaType] = useState("allowance");
  const [activeTemplate, setActiveTemplate] = useState("conditional");
  const [showAdvancedFns, setShowAdvancedFns] = useState(false);

  useEffect(() => {
    entGetCatalog().then(setCatalog).catch(() => {});
  }, []);

  useEffect(() => {
    if (pipelineFormulas) return undefined;
    let cancelled = false;
    v2ListComponents()
      .then((rows) => {
        if (!cancelled) setSalaryComponents(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pipelineFormulas]);

  useEffect(() => {
    if (!editingComponent) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fromUrl = String(searchParams.get("expression") || "").trim();
        if (fromUrl) {
          setExpr(fromUrl);
          return;
        }
        const rows = await v2ListComponents();
        const comp = (Array.isArray(rows) ? rows : []).find(
          (c) => String(c.component_id) === String(toolCtx.componentId),
        );
        if (cancelled || !comp) return;
        setExpr(formulaOf(comp) || comp.formula_expression || "");
      } catch {
        /* optional preload */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingComponent, toolCtx.componentId, searchParams]);

  useEffect(() => {
    if (!pipelineFormulas) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const state = readPipelineState();
        writePipelineState({ currentStep: PIPELINE_STEPS.formulas });
        const [rows, cfgRes] = await Promise.all([
          v2ListComponents(),
          API.get("/organisation/me/extra-work-config").catch(() => null),
        ]);
        const extra = cfgRes?.data?.extra_work || null;
        const visible = filterComponentsForPipeline(rows, extra);
        const ids = new Set((state.allocatedComponentIds || []).map(String));
        const allocated = visible.filter((r) => ids.has(String(r.component_id)));
        if (cancelled) return;
        setPipelineComps(allocated);
        if (!primedRef.current && allocated[0]) {
          primedRef.current = true;
          const first = allocated[0];
          const id = String(first.component_id);
          setPipelineActiveId(id);
          setExpr(componentFormula(first, state.formulas));
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load template components");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineFormulas, toast]);

  const activeComp = pipelineComps.find((c) => String(c.component_id) === String(pipelineActiveId));
  const editingName = pipelineFormulas
    ? activeComp?.name || "Component"
    : toolCtx.componentCode || "Component";

  const insertCodes = useMemo(() => {
    const source = pipelineFormulas ? pipelineComps : salaryComponents;
    const seen = new Set();
    const codes = [];
    const add = (code, label) => {
      const key = String(code || "").trim().toUpperCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      codes.push({ code: key, label: label || key });
    };
    add("GROSS", "Gross");
    for (const comp of source) {
      add(componentCode(comp), comp.name);
    }
    return codes;
  }, [pipelineFormulas, pipelineComps, salaryComponents]);

  function selectPipelineComponent(comp) {
    const id = String(comp.component_id);
    setPipelineActiveId(id);
    setExpr(componentFormula(comp, readPipelineState().formulas));
    setValidation(null);
    setSimResult(null);
  }

  function onMonthlyGrossChange(value) {
    setMonthlyGross(value);
    const monthly = Number(String(value || "").replace(/,/g, ""));
    if (pipelineFormulas) {
      writePipelineState({
        targetAnnualCtc: Number.isFinite(monthly) && monthly > 0 ? Math.round(monthly * 12) : null,
      });
    }
    setSimResult(null);
  }

  function checkContext() {
    const monthly = Number(String(monthlyGross || "").replace(/,/g, ""));
    const gross = Number.isFinite(monthly) && monthly > 0 ? monthly : 0;
    const ctx = { GROSS: gross, MONTHLY_CTC: gross, CTC: gross, ANNUAL_CTC: gross * 12 };
    return ctx;
  }

  async function savePipelineComponentFormula() {
    if (!pipelineActiveId) return null;
    const res = await validate();
    const comp = pipelineComps.find((c) => String(c.component_id) === pipelineActiveId);
    if (!comp) return null;
    const calc = String(comp.calculation_type || "").toLowerCase();
    if (calc === "formula" && !res?.is_valid) return null;

    setPipelineSaving(true);
    try {
      const expression = String(expr || "").trim();
      writePipelineState({
        formulas: {
          [pipelineActiveId]: { expression },
        },
      });
      setPipelineComps((prev) =>
        prev.map((c) =>
          String(c.component_id) === pipelineActiveId
            ? { ...c, formula_expression: expression, calculation_type: "formula" }
            : c,
        ),
      );
      toast.success(`Formula saved for ${comp.name}`);
      return res;
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not save formula");
      return null;
    } finally {
      setPipelineSaving(false);
    }
  }

  function continuePipelineFromFormulas() {
    const state = readPipelineState();
    if (!state.allocatedComponentIds?.length) {
      toast.error("No allocated components — go back to component allocation.");
      return;
    }
    writePipelineState({
      returnToReview: false,
      currentStep: PIPELINE_STEPS.ctcAllocation,
    });
    navigate(pipelineStepPath(PIPELINE_STEPS.ctcAllocation));
  }

  function insertToken(token) {
    setExpr((p) => (p ? `${p} ${token}` : token));
    setValidation(null);
    setSimResult(null);
  }

  function applyExpression(expression, templateId = null) {
    if (!expression) {
      setShowAdvancedFns(true);
      setActiveTemplate("advanced");
      return;
    }
    setExpr(expression);
    setValidation(null);
    setSimResult(null);
    if (templateId) setActiveTemplate(templateId);
    if (templateId !== "advanced") setShowAdvancedFns(false);
  }

  const variableCodes = useMemo(() => {
    const fromCatalog = catalog?.formula_variables;
    return Array.isArray(fromCatalog) && fromCatalog.length ? fromCatalog : FALLBACK_VARIABLES;
  }, [catalog]);

  const functionNames = useMemo(() => {
    const fromCatalog = catalog?.formula_functions;
    return Array.isArray(fromCatalog) && fromCatalog.length
      ? fromCatalog
      : ["if", "round", "min", "max", "sum", "percentage"];
  }, [catalog]);

  const groupedVariables = useMemo(() => groupVariables(variableCodes), [variableCodes]);

  const progressDone = {
    1: Boolean(formulaType),
    2: Boolean(String(expr || "").trim()),
    3: Boolean(validation?.is_valid || simResult),
  };
  const activeProgress = !progressDone[1] ? 1 : !progressDone[2] ? 2 : 3;

  async function validate() {
    try {
      setLoading(true);
      const res = await v2ValidateFormula({
        expression: expr,
        known_identifiers: catalog?.formula_variables || [],
        strict_unknown_identifiers: false,
      });
      setValidation(res);
      if (!res.is_valid) toast.error(res.error || "Invalid formula");
      else toast.success("Formula is valid");
      return res;
    } catch (e) {
      toast.error(e?.detail || e?.message || "Validation failed");
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function simulate({ quiet = false } = {}) {
    const expression = String(expr || "").trim();
    if (!expression) {
      setSimResult(null);
      return null;
    }
    try {
      if (!quiet) setLoading(true);
      const sample = pipelineFormulas ? checkContext() : SAMPLE_CTX;
      const monthly = Number(String(monthlyGross || "").replace(/,/g, ""));
      if (pipelineFormulas && !(Number.isFinite(monthly) && monthly > 0)) {
        setSimResult(null);
        return null;
      }
      const res = await entSimulateFormula({ expression, sample_context: sample });
      setSimResult(res);
      if (!quiet) toast.success(pipelineFormulas ? "Result updated" : "Preview ready");
      return res;
    } catch (e) {
      setSimResult(null);
      if (!quiet) toast.error(e?.detail || e?.message || "Could not calculate this formula");
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  async function checkFormula() {
    const res = await validate();
    if (res?.is_valid) await simulate();
  }

  // Auto-evaluate formula result in the structure pipeline when gross/expression change.
  useEffect(() => {
    if (!pipelineFormulas) return undefined;
    const expression = String(expr || "").trim();
    const monthly = Number(String(monthlyGross || "").replace(/,/g, ""));
    if (!expression || !(Number.isFinite(monthly) && monthly > 0)) {
      setSimResult(null);
      return undefined;
    }
    const t = setTimeout(() => {
      simulate({ quiet: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional debounce on expr/gross
  }, [pipelineFormulas, expr, monthlyGross]);

  async function applyFormulaToComponent() {
    const res = await validate();
    if (!res?.is_valid || !toolCtx.componentId) return;
    applyFormulaToComponentAndReturn(navigate, {
      componentId: toolCtx.componentId,
      expression: expr,
      returnTo: toolCtx.returnTo || componentEditPath(toolCtx.componentId),
    });
    toast.success(`Formula applied to ${toolCtx.componentCode || "component"}. Save on the edit page to persist.`);
  }

  async function saveFormula() {
    if (pipelineFormulas) {
      await savePipelineComponentFormula();
      return;
    }
    if (editingComponent) {
      await applyFormulaToComponent();
      return;
    }
    const res = await validate();
    if (!res?.is_valid) return;
    try {
      await navigator.clipboard?.writeText(String(expr || "").trim());
    } catch {
      /* clipboard optional */
    }
    toast.success("Formula ready — open Derived Variables to save it permanently");
    navigate("/salary-v2/derived-variables", { state: { expression: String(expr || "").trim() } });
  }

  const resultValue = simResult?.result;
  const resultDisplay =
    resultValue === null || resultValue === undefined
      ? "—"
      : Number.isFinite(Number(resultValue))
        ? money(resultValue)
        : String(resultValue);

  const grossLabel = (() => {
    const monthly = Number(String(monthlyGross || "").replace(/,/g, ""));
    return Number.isFinite(monthly) && monthly > 0 ? money(monthly) : "Enter gross";
  })();

  return (
    <div className={`fb${pipelineFormulas ? " fb--pipeline" : ""}`}>
      {pipelineFormulas ? (
        <>
          <SalaryTemplatePipelineBar currentStep={PIPELINE_STEPS.formulas} compact />
      <header className="fb-hero">
        <h2 className="fb-hero-title">Formula Builder</h2>
        <p className="fb-hero-sub">
          Edit the formula, then check the result against monthly gross. Only components already on this structure are listed.
        </p>
      </header>

      <div className="fb-work">
        {pipelineFormulas ? (
          <section className="fb-card fb-comps" aria-label="Components on this structure">
            <h3 className="fb-card-title">Components</h3>
            <div className="fb-comp-list">
              {pipelineComps.map((c) => {
                const id = String(c.component_id);
                const selected = id === String(pipelineActiveId);
                const stored = componentFormula(c, readPipelineState().formulas);
                const fx = selected ? String(expr || "").trim() : stored;
                const needsFormula = componentNeedsFormula(c, fx);
                const earning = isEarningSection(c);
                const shown = needsFormula ? "" : readableFormula(fx || mappingText(c));
                return (
                  <button
                    key={id}
                    type="button"
                    className={`fb-comp${selected ? " is-selected" : ""}${needsFormula ? " is-needs-formula" : ""}`}
                    onClick={() => selectPipelineComponent(c)}
                    aria-pressed={selected}
                  >
                    <span className="fb-comp-id">
                      <strong>{c.name}</strong>
                      <em className={`fb-kind${earning ? " is-earn" : " is-ded"}`}>{earning ? "Earning" : "Deduction"}</em>
                    </span>
                    <span className="fb-comp-fx">{shown || "No formula yet"}</span>
                    {needsFormula ? (
                      <span className="fb-comp-hint">Add a formula when you are ready — payroll can wait.</span>
                    ) : null}
                  </button>
                );
              })}
              {!pipelineComps.length ? (
                <p className="fb-card-hint">No components yet. Go back and select components.</p>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="fb-card fb-editor" aria-label="Formula editor">
          <h3 className="fb-card-title">{editingName}</h3>
          {pipelineFormulas && activeComp && componentNeedsFormula(activeComp, expr) ? (
            <p className="fb-editor-nudge">This component has no formula yet. Add one here if you would like payroll to calculate it.</p>
          ) : null}
          <textarea
            className="fb-textarea"
            rows={3}
            value={expr}
            onChange={(e) => {
              setExpr(e.target.value);
              setValidation(null);
              setSimResult(null);
            }}
            placeholder="Example: BASIC * 0.40"
            spellCheck={false}
            aria-label="Calculation formula"
          />

          <div className="fb-chip-row" aria-label="Insert existing values">
            {insertCodes.map((item) => (
              <button
                key={item.code}
                type="button"
                className="fb-chip"
                onClick={() => insertToken(item.code)}
                title={`Insert ${item.code}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="fb-actions">
            <button type="button" className="fb-btn fb-btn-primary" disabled={loading || !expr.trim()} onClick={checkFormula}>
              <ClipboardCheck size={15} />
              Check formula
            </button>
            <button type="button" className="fb-btn fb-btn-secondary" disabled={loading || !expr.trim()} onClick={simulate}>
              <Eye size={15} />
              Preview
            </button>
            <button
              type="button"
              className="fb-btn fb-btn-success"
              disabled={loading || pipelineSaving || !expr.trim() || (pipelineFormulas && !pipelineActiveId)}
              onClick={saveFormula}
            >
              <Save size={15} />
              {pipelineSaving ? "Saving…" : "Save formula"}
            </button>
          </div>

          {validation?.is_valid ? (
            <div className="fb-success">
              <CheckCircle2 size={16} />
              <span>Formula is valid{validation.dependencies?.length ? ` · uses ${validation.dependencies.join(", ")}` : ""}</span>
            </div>
          ) : null}
          {validation && !validation.is_valid ? (
            <div className="fb-error">
              <AlertCircle size={16} />
              <span>{validation.error || "This formula could not be checked."}</span>
            </div>
          ) : null}
        </section>

        <aside className="fb-card fb-check" aria-label="Check formula against gross">
          <h3 className="fb-card-title">Check</h3>
          <label className="fb-gross-field">
            <span>Monthly gross</span>
            <input
              inputMode="numeric"
              placeholder="50000"
              value={monthlyGross}
              onChange={(e) => onMonthlyGrossChange(e.target.value)}
              aria-label="Monthly gross salary"
            />
          </label>
          <div className="fb-result-box">
            <div className="fb-result-label">Formula result</div>
            <div className="fb-result-value">{resultDisplay}</div>
            <div className="fb-result-gross">Against gross {grossLabel}</div>
          </div>
        </aside>
      </div>

      {pipelineFormulas ? (
        <StructureGrossPanel
          compact
          components={pipelineComps}
          formulas={{
            ...readPipelineState().formulas,
            ...(pipelineActiveId ? { [pipelineActiveId]: { expression: String(expr || "").trim() } } : {}),
          }}
          monthlyGross={monthlyGross}
          onMonthlyGrossChange={onMonthlyGrossChange}
        />
      ) : null}

      {pipelineFormulas ? (
        <div className="stp-pipeline-sticky">
          <button
            type="button"
            className="stp-btn stp-btn-ghost"
            onClick={() => navigate(pipelineStepPath(PIPELINE_STEPS.components, readPipelineState()))}
          >
            Back
          </button>
          <button type="button" className="stp-btn stp-btn-primary" onClick={continuePipelineFromFormulas}>
            Next: Gross salary <ArrowRight size={16} />
          </button>
        </div>
      ) : null}
        </>
      ) : (
        <>
          {editingComponent ? (
            <PayrollComponentToolsBar
              activePage="formula"
              componentId={toolCtx.componentId || null}
              componentCode={toolCtx.componentCode}
            />
          ) : null}
          <header className="fb-hero">
            <div className="fb-hero-eyebrow">
              <Calculator size={13} />
              BrixiGo Payroll
            </div>
            <h2 className="fb-hero-title">Formula Builder</h2>
            <p className="fb-hero-sub">
              Create, test and validate payroll formulas in simple steps.
            </p>
            <div className="fb-progress" aria-label="Formula builder progress">
              {PROGRESS_STEPS.map((step) => {
                const done = progressDone[step.id];
                const active = activeProgress === step.id;
                return (
                  <div
                    key={step.id}
                    className={`fb-progress-step${active ? " is-active" : ""}${done ? " is-done" : ""}`}
                  >
                    <span className="fb-progress-num">
                      {done ? <CheckCircle2 size={12} /> : step.id}
                    </span>
                    {step.label}
                  </div>
                );
              })}
            </div>
          </header>

          <div className="fb-layout">
            <div className="fb-main">
              <section className="fb-card" aria-labelledby="fb-step1-title">
                <div className="fb-card-head">
                  <div>
                    <h3 className="fb-card-title" id="fb-step1-title">
                      <span className="fb-step-badge">1</span>
                      Choose Formula Type
                    </h3>
                    <p className="fb-card-hint">Tell us what you are calculating — this keeps the workspace focused.</p>
                  </div>
                </div>
                <div className="fb-type-grid">
                  {FORMULA_TYPES.map((type) => {
                    const Icon = type.icon;
                    const selected = formulaType === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        className={`fb-type-card${selected ? " is-selected" : ""}`}
                        onClick={() => setFormulaType(type.id)}
                        aria-pressed={selected}
                      >
                        <span className="fb-type-icon">
                          <Icon size={16} />
                        </span>
                        <span className="fb-type-label">{type.label}</span>
                        <span className="fb-type-desc">{type.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="fb-card" aria-labelledby="fb-step2-title">
                <div className="fb-card-head">
                  <div>
                    <h3 className="fb-card-title" id="fb-step2-title">
                      <span className="fb-step-badge">2</span>
                      Choose Payroll Values
                    </h3>
                    <p className="fb-card-hint">Click any value to insert it into your calculation formula.</p>
                  </div>
                </div>

                {groupedVariables.map((group) => (
                  <div key={group.category} className="fb-cat-block">
                    <div className="fb-cat-label">
                      <span className="fb-cat-dot" style={{ background: group.color }} />
                      {group.category}
                    </div>
                    <div className="fb-chip-row">
                      {group.codes.map((code) => (
                        <button
                          key={code}
                          type="button"
                          className="fb-chip"
                          onClick={() => insertToken(code)}
                          title={`Insert ${code}`}
                        >
                          {friendlyLabel(code)}
                          <span className="fb-chip-code">{code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </section>

              <section className="fb-card" aria-labelledby="fb-tmpl-title">
                <div className="fb-card-head">
                  <div>
                    <h3 className="fb-card-title" id="fb-tmpl-title">
                      <span className="fb-step-badge">2</span>
                      Formula Templates
                    </h3>
                    <p className="fb-card-hint">Start from a ready-made pattern — then fine-tune the numbers.</p>
                  </div>
                </div>

                <div className="fb-tmpl-grid">
                  {TEMPLATES.map((tmpl) => (
                    <button
                      key={tmpl.id}
                      type="button"
                      className={`fb-tmpl${activeTemplate === tmpl.id ? " is-active" : ""}`}
                      onClick={() => {
                        if (tmpl.id === "advanced") {
                          setShowAdvancedFns(true);
                          setActiveTemplate("advanced");
                          return;
                        }
                        applyExpression(tmpl.expression, tmpl.id);
                      }}
                    >
                      <span className="fb-tmpl-label">{tmpl.label}</span>
                      <span className="fb-tmpl-hint">{tmpl.hint}</span>
                    </button>
                  ))}
                </div>

                {(showAdvancedFns || activeTemplate === "advanced") && (
                  <div className="fb-fn-row" aria-label="Advanced functions">
                    {functionNames.map((f) => (
                      <button
                        key={f}
                        type="button"
                        className="fb-fn-chip"
                        onClick={() => insertToken(`${f}(`)}
                      >
                        {f}()
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="fb-card" aria-labelledby="fb-editor-title">
                <h3 className="fb-editor-label" id="fb-editor-title">
                  Calculation Formula
                </h3>
                <p className="fb-editor-guide">
                  Build your payroll formula by selecting values and templates.
                </p>

                <textarea
                  className="fb-textarea"
                  rows={4}
                  value={expr}
                  onChange={(e) => {
                    setExpr(e.target.value);
                    setValidation(null);
                    setSimResult(null);
                  }}
                  placeholder="Example: BASIC * 0.40"
                  spellCheck={false}
                />

                <div className="fb-example-box">
                  <strong>Example</strong>
                  <span className="fb-ex-line">Basic Salary × 40%</span>
                  <span style={{ display: "block", marginTop: "0.35rem", color: "#64748b" }}>
                    If Gross Salary &gt; ₹50,000 → Then ₹5,000 · Else ₹2,500
                  </span>
                </div>

                <div className="fb-actions">
                  <button type="button" className="fb-btn fb-btn-primary" disabled={loading || !expr.trim()} onClick={validate}>
                    <ClipboardCheck size={16} />
                    Check Formula
                  </button>
                  <button type="button" className="fb-btn fb-btn-secondary" disabled={loading || !expr.trim()} onClick={simulate}>
                    <Eye size={16} />
                    Preview Result
                  </button>
                  <button type="button" className="fb-btn fb-btn-success" disabled={loading || !expr.trim()} onClick={saveFormula}>
                    <Save size={16} />
                    {editingComponent ? "Use formula on component" : "Save Formula"}
                  </button>
                </div>

                {validation?.dependencies?.length ? (
                  <p className="fb-deps">Uses: {validation.dependencies.join(", ")}</p>
                ) : null}
              </section>
            </div>

            <aside className="fb-side">
              <div className="fb-side-card">
                <h3 className="fb-side-title">
                  <Eye size={16} style={{ color: "#2563eb" }} />
                  Live Preview
                </h3>
                <p style={{ margin: "0 0 0.65rem", fontSize: "0.78rem", color: "#64748b" }}>Sample Values</p>
                <div className="fb-preview-rows">
                  <div className="fb-preview-row">
                    <span>Basic Salary</span>
                    <strong>{money(SAMPLE_CTX.BASIC)}</strong>
                  </div>
                  <div className="fb-preview-row">
                    <span>Gross Salary</span>
                    <strong>{money(SAMPLE_CTX.GROSS)}</strong>
                  </div>
                  <div className="fb-preview-row">
                    <span>Monthly Gross</span>
                    <strong>{money(SAMPLE_CTX.MONTHLY_CTC)}</strong>
                  </div>
                </div>

                <div className="fb-result-box">
                  <div className="fb-result-label">Formula Result</div>
                  <div className="fb-result-value">{resultDisplay}</div>
                </div>

                {validation?.is_valid ? (
                  <div className="fb-success">
                    <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <div className="fb-success-title">Formula is valid</div>
                      <div className="fb-success-text">Syntax looks good. Preview the result before saving.</div>
                    </div>
                  </div>
                ) : null}

                {validation && !validation.is_valid ? (
                  <div className="fb-error">
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>{validation.error || "This formula could not be validated. Please review the expression."}</div>
                  </div>
                ) : null}
              </div>

              <div className="fb-side-card">
                <h3 className="fb-side-title">
                  <Sparkles size={16} style={{ color: "#7c3aed" }} />
                  Popular Formula Examples
                </h3>
                <div className="fb-example-list">
                  {POPULAR_EXAMPLES.map((ex) => (
                    <div key={ex.expression} className="fb-example-item">
                      <div>
                        <p>{ex.label}</p>
                        <small>{ex.expression}</small>
                      </div>
                      <button
                        type="button"
                        className="fb-use-btn"
                        onClick={() => applyExpression(ex.expression)}
                      >
                        Use
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="fb-help">
                <h3 className="fb-help-title">
                  <HelpCircle size={16} style={{ color: "#2563eb" }} />
                  Need Help?
                </h3>
                <ul>
                  <li>Click any salary value to insert it.</li>
                  <li>Choose a template to speed up formula creation.</li>
                  <li>Validate before saving.</li>
                </ul>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
